import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import type {
  GatewaySite,
  SiteListResponse,
  CreateSiteRequest,
  UpdateSiteRequest,
} from '../types/site';

export const siteKeys = {
  all: ['sites'] as const,
  list: () => [...siteKeys.all, 'list'] as const,
  detail: (id: string) => [...siteKeys.all, 'detail', id] as const,
};

export function useSites() {
  return useQuery({
    queryKey: siteKeys.list(),
    queryFn: () => apiFetch<SiteListResponse>('/api/sites'),
    staleTime: 60_000,
  });
}

export function useCreateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSiteRequest) =>
      apiFetch<GatewaySite>('/api/sites', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.list() });
    },
  });
}

export function useUpdateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateSiteRequest & { id: string }) =>
      apiFetch<GatewaySite>(`/api/sites/${id}`, {
        method: 'PUT',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.list() });
    },
  });
}

export function useDeleteSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/sites/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.list() });
    },
  });
}
